import { apiService } from './api'

export type AnnouncementTargetRole = 'CORPORATE_ADMIN' | 'USER' | 'ALL'
export type AnnouncementStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'ARCHIVED'
export type AnnouncementPriority = 'LOW' | 'MEDIUM' | 'HIGH'

export interface Announcement {
  id: string
  title: string
  content: string
  summary?: string
  targetRole: AnnouncementTargetRole
  targetTenantIds: string[]
  priority: AnnouncementPriority
  status: AnnouncementStatus
  isPinned: boolean
  publishedAt?: string
  scheduledAt?: string
  expiresAt?: string
  emailNotification: boolean
  emailSentAt?: string
  emailSentCount: number
  createdBy: string
  createdAt: string
  updatedAt: string
  creator?: { id: string; name: string; email: string }
  isRead?: boolean
  readAt?: string
  _count?: { readLogs: number }
}

export interface CreateAnnouncementRequest {
  title: string
  content: string
  summary?: string
  targetRole: AnnouncementTargetRole
  targetTenantIds?: string[]
  priority?: AnnouncementPriority
  isPinned?: boolean
  scheduledAt?: string
  expiresAt?: string
  emailNotification?: boolean
}

export interface UpdateAnnouncementRequest {
  title?: string
  content?: string
  summary?: string
  targetRole?: AnnouncementTargetRole
  targetTenantIds?: string[]
  priority?: AnnouncementPriority
  isPinned?: boolean
  scheduledAt?: string
  expiresAt?: string
  emailNotification?: boolean
}

export interface AnnouncementListResponse {
  announcements: Announcement[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AnnouncementStats {
  announcementId: string
  readCount: number
  targetUserCount: number
  readRate: number
}

class AnnouncementService {
  // 管理用API（SUPER_ADMIN）
  async getAnnouncements(params?: {
    status?: AnnouncementStatus
    targetRole?: AnnouncementTargetRole
    page?: number
    limit?: number
  }): Promise<AnnouncementListResponse> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.set('status', params.status)
    if (params?.targetRole) queryParams.set('targetRole', params.targetRole)
    if (params?.page) queryParams.set('page', String(params.page))
    if (params?.limit) queryParams.set('limit', String(params.limit))

    const query = queryParams.toString()
    return apiService.get(`/announcements${query ? `?${query}` : ''}`)
  }

  async getAnnouncementById(id: string): Promise<{ announcement: Announcement }> {
    return apiService.get(`/announcements/${id}`)
  }

  async createAnnouncement(
    data: CreateAnnouncementRequest
  ): Promise<{ message: string; announcement: Announcement }> {
    return apiService.post('/announcements', data)
  }

  async updateAnnouncement(
    id: string,
    data: UpdateAnnouncementRequest
  ): Promise<{ message: string; announcement: Announcement }> {
    return apiService.put(`/announcements/${id}`, data)
  }

  async deleteAnnouncement(id: string): Promise<{ message: string }> {
    return apiService.delete(`/announcements/${id}`)
  }

  async publishAnnouncement(id: string): Promise<{ message: string; announcement: Announcement }> {
    return apiService.post(`/announcements/${id}/publish`)
  }

  async archiveAnnouncement(id: string): Promise<{ message: string; announcement: Announcement }> {
    return apiService.post(`/announcements/${id}/archive`)
  }

  async getAnnouncementStats(id: string): Promise<{ stats: AnnouncementStats }> {
    return apiService.get(`/announcements/${id}/stats`)
  }

  // ユーザー向けAPI
  async getActiveAnnouncements(): Promise<{ announcements: Announcement[] }> {
    return apiService.get('/announcements/active')
  }

  async markAsRead(id: string): Promise<{ message: string }> {
    return apiService.post(`/announcements/${id}/read`)
  }

  async getUnreadCount(): Promise<{ count: number }> {
    return apiService.get('/announcements/unread-count')
  }
}

export const announcementService = new AnnouncementService()
